import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { uploadFile, fileUrl } from "@/api";
import { toast } from "sonner";
import { Loader2, Upload, X } from "lucide-react";

export default function UploadImage({ value, onChange, label = "Upload Image", testid = "upload-image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const pick = () => inputRef.current?.click();

  const uploadFromFile = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Only image files are allowed");
      return;
    }
    setBusy(true);
    try {
      const res = await uploadFile(file);
      onChange?.(res.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    await uploadFromFile(file);
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dragOver) setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    await uploadFromFile(file);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={pick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); pick(); } }}
      onDragEnter={handleDragOver}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={
        "flex items-center gap-3 rounded-lg border border-dashed p-2 transition-colors cursor-pointer " +
        (dragOver
          ? "border-[#E8692A] bg-[#FDECE2]"
          : "border-transparent hover:border-slate-200")
      }
      data-testid={testid}
      data-dragover={dragOver ? "true" : "false"}
    >
      {value ? (
        <div className="relative">
          <img
            src={fileUrl(value)}
            alt="preview"
            className="h-16 w-16 rounded-md object-cover border border-slate-200"
            data-testid={`${testid}-preview`}
          />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange?.(null); }}
            className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-0.5 hover:bg-slate-50"
            data-testid={`${testid}-remove`}
            aria-label="Remove image"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div
          className="h-16 w-16 rounded-md border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400 text-xs pointer-events-none select-none"
          data-testid={`${testid}-placeholder`}
        >
          {dragOver ? "Drop here" : "No image"}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testid}-input`}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={(e) => { e.stopPropagation(); pick(); }}
        disabled={busy}
        data-testid={`${testid}-button`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
        {label}
      </Button>
    </div>
  );
}
